import Cookies from 'js-cookie'
import jwtDefaultConfig from '@src/@core/auth/jwt/jwtDefaultConfig'

const DEFAULT_CLOCK_SKEW_SECONDS = 30
const LAST_ACTIVITY_KEY = 'lastActivityAt'

const parseJsonSafely = (value) => {
  if (!value) return null

  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const normalizeBase64Url = (value) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padding = normalized.length % 4

  if (!padding) return normalized

  return `${normalized}${'='.repeat(4 - padding)}`
}

const getCookieDomains = () => {
  if (typeof window === 'undefined') return []

  const host = window.location.hostname
  const parts = host.split('.')
  const domains = new Set([host])

  for (let index = 0; index < parts.length - 1; index += 1) {
    domains.add(`.${parts.slice(index).join('.')}`)
  }

  return Array.from(domains)
}

const getCookiePaths = () => {
  if (typeof window === 'undefined') return ['/']

  const segments = window.location.pathname.split('/').filter(Boolean)
  const paths = new Set(['/'])
  let currentPath = ''

  segments.forEach(segment => {
    currentPath = `${currentPath}/${segment}`
    paths.add(currentPath)
  })

  return Array.from(paths)
}

const removeCookieEverywhere = (cookieName) => {
  const paths = getCookiePaths()
  const domains = getCookieDomains()

  paths.forEach(path => {
    Cookies.remove(cookieName, { path })
    domains.forEach(domain => {
      Cookies.remove(cookieName, { path, domain })
    })
  })

  if (typeof document !== 'undefined') {
    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
  }
}

export const getStoredUserData = () => parseJsonSafely(localStorage.getItem('userData'))

export const getStoredAccessToken = () => {
  const tokenFromStorage = localStorage.getItem(jwtDefaultConfig.storageTokenKeyName)

  if (tokenFromStorage) return tokenFromStorage

  return getStoredUserData()?.accessToken || null
}

export const getStoredRefreshToken = () => {
  const tokenFromStorage = localStorage.getItem(jwtDefaultConfig.storageRefreshTokenKeyName)

  if (tokenFromStorage) return tokenFromStorage

  return getStoredUserData()?.refreshToken || null
}

export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null

  const parts = token.split('.')

  if (parts.length < 2) return null

  try {
    return parseJsonSafely(atob(normalizeBase64Url(parts[1])))
  } catch (error) {
    return null
  }
}

export const isJwtExpired = (token, clockSkewSeconds = DEFAULT_CLOCK_SKEW_SECONDS) => {
  const payload = decodeJwtPayload(token)

  if (!payload?.exp) return false

  return Date.now() >= (payload.exp - clockSkewSeconds) * 1000
}

export const hasSessionArtifacts = () => {
  return Boolean(
    localStorage.getItem('userData') ||
    localStorage.getItem(jwtDefaultConfig.storageTokenKeyName) ||
    localStorage.getItem(jwtDefaultConfig.storageRefreshTokenKeyName) ||
    localStorage.getItem('persist:root')
  )
}

export const isUserLoggedIn = () => {
  const userData = getStoredUserData()
  const accessToken = getStoredAccessToken()
  const refreshToken = getStoredRefreshToken()

  if (!userData) return false
  if (accessToken && !isJwtExpired(accessToken)) return true

  return Boolean(refreshToken)
}

export const clearClientCookies = () => {
  const cookieNames = Object.keys(Cookies.get())

  cookieNames.forEach(removeCookieEverywhere)

  if (typeof document !== 'undefined') {
    document.cookie.split(';').forEach(cookie => {
      const cookieName = cookie.split('=')[0]?.trim()
      if (cookieName) {
        removeCookieEverywhere(cookieName)
      }
    })
  }
}

export const clearClientSession = () => {
  clearClientCookies()

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.clear()
  }

  localStorage.clear()
}

export const sessionActivityKey = LAST_ACTIVITY_KEY